/**
 * ModularPatientDashboard.js
 *
 * Service-user dashboard for patients on subcollection-based assessment modules
 * (Adult Autism, future CYP). Renders the same visual layout as the ADHD
 * PatientDashboard — PatientHeader with gradient, QuestionnaireAccordion with
 * timeline cards, auto-scroll to next incomplete form — but sources data from
 * ModularPatientSelfDataContext (formInstances subcollection) rather than the
 * monolithic patientDataModel.
 *
 * Architecture: A "synthetic patientDataModel" adapter satisfies the accordion's
 * two reads (informant gate + time estimates) without any modifications to the
 * shared QuestionnaireAccordion component. This keeps ADHD code regression-free.
 */
import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { ModularPatientSelfDataContext } from '../../../context/ModularPatientSelfDataContext';
import { useAuth } from '../../../hooks/useAuth';
import PatientHeader from './components/PatientHeader';
import QuestionnaireAccordion from './components/QuestionnaireAccordion';
import AutismInformantSetupForm from './components/AutismInformantSetupForm';
import PatientManageInformantsForm from './components/PatientManageInformantsForm';
import AllFormsCompletedFeedbackModal from '../3_forms/components/AllFormsCompletedFeedbackModal';
import {
  mapFormInstancesToAssessments,
  computeNextQuestionnaireIndex,
  buildSyntheticPatientDataModel,
  groupAutismAssessments,
  AUTISM_PATIENT_FORM_GROUPS,
} from '../../../utils/modularAssessmentMapper';

export default function ModularPatientDashboard() {
  const { header, formInstances, loading, fetchPatientData } = useContext(ModularPatientSelfDataContext);
  const { claims } = useAuth();

  const [isRefreshing, setIsRefreshing] = useState(true);
  const [showInformantSetup, setShowInformantSetup] = useState(false);
  const [showInformantManagement, setShowInformantManagement] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const scrollContainerRef = useRef(null);
  const nextQuestionnaireRef = useRef(null);

  const userOrganisation = header?.organisation || claims?.organisation || '';

  // Refresh on mount to get latest data from Firestore. Only mark refresh
  // complete when fetchPatientData actually ran (not a no-op from enabled=false).
  useEffect(() => {
    let cancelled = false;
    setIsRefreshing(true);
    const result = fetchPatientData();
    if (result && typeof result.finally === 'function') {
      result.finally(() => {
        if (!cancelled) setIsRefreshing(false);
      });
    } else {
      setIsRefreshing(false);
    }
    return () => { cancelled = true; };
  }, [fetchPatientData]);

  // Build assessment objects for the accordion from modular formInstances
  const assessments = useMemo(
    () => mapFormInstancesToAssessments(formInstances, '/service-user/form'),
    [formInstances]
  );

  // Group flat assessments into thematic accordion sections matching ADHD's
  // visual structure (About You, Health and Wellbeing, Childhood, Adult Life)
  const groupedAssessments = useMemo(
    () => groupAutismAssessments(assessments, AUTISM_PATIENT_FORM_GROUPS),
    [assessments]
  );

  // Synthetic patientDataModel satisfies QuestionnaireAccordion's property reads
  // (hasCompletedInformantSetupForm for the informant gate, formData[key].progress
  // for time estimate calculations) without touching the shared component.
  const syntheticPatientDataModel = useMemo(
    () => buildSyntheticPatientDataModel(header, formInstances),
    [header, formInstances]
  );

  // Index of the next incomplete form — drives auto-expand and violet pulse
  const nextIdx = useMemo(
    () => computeNextQuestionnaireIndex(groupedAssessments),
    [groupedAssessments]
  );

  // Auto-scroll to the next incomplete questionnaire once data loads
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (!isRefreshing && assessments.length > 0) {
      setIsDataLoaded(true);
    }
  }, [isRefreshing, assessments]);

  // Determine if the feedback modal should show: all forms completed AND no prior feedback
  const shouldShowFeedbackModal = useMemo(() => {
    if (!formInstances || Object.keys(formInstances).length === 0) return false;
    const allCompleted = Object.values(formInstances).every(fi => fi?.status === 'completed');
    const feedbackAlreadySubmitted = header?.formFeedback?.submittedAt;
    return allCompleted && !feedbackAlreadySubmitted;
  }, [formInstances, header]);

  // Show modal after a brief delay once all conditions are met
  useEffect(() => {
    if (shouldShowFeedbackModal && isDataLoaded) {
      const timer = setTimeout(() => setShowFeedbackModal(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowFeedbackModal, isDataLoaded]);

  useEffect(() => {
    if (!isDataLoaded || !scrollContainerRef.current) return;

    const scrollTimeout = setTimeout(() => {
      if (nextQuestionnaireRef.current && scrollContainerRef.current) {
        const targetRect = nextQuestionnaireRef.current.getBoundingClientRect();
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const scrollOffset = 16;
        const scrollPosition = scrollContainerRef.current.scrollTop + targetRect.top - containerRect.top - scrollOffset;

        scrollContainerRef.current.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });
      }
    }, 300);

    return () => clearTimeout(scrollTimeout);
  }, [isDataLoaded, nextIdx]);

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* Fixed Header with Shadow Overlay */}
      <div className="flex-shrink-0 relative z-10">
        <PatientHeader
          useModularHeader
          assessments={assessments}
          onInformantsClick={() => setShowInformantManagement(true)}
        />
        {/* White gradient shadow below header */}
        <div className="absolute bottom-0 left-0 right-0 h-4 pointer-events-none transform translate-y-full z-20">
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-transparent"></div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative z-0">
        {/* Skeleton: accordion card placeholders while data loads */}
        <div className={`px-4 pt-4 space-y-3 transition-opacity duration-300 ease-in-out ${
          isRefreshing ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        }`}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-2/5 rounded bg-gray-200" />
                  <div className="h-3 w-1/3 rounded bg-gray-100" />
                </div>
                <div className="h-8 w-8 rounded-full bg-gray-200" />
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-gray-100" />
            </div>
          ))}
        </div>

        {/* Real content: questionnaire accordion */}
        {!isRefreshing && (
          <div className="animate-fadeIn">
            <QuestionnaireAccordion
              assessments={groupedAssessments}
              patientDataModel={syntheticPatientDataModel}
              nextQuestionnaireIndex={nextIdx}
              nextQuestionnaireRef={nextQuestionnaireRef}
              isInformant={false}
              userOrganisation={userOrganisation}
              showInformantSetup={showInformantSetup}
              setShowInformantSetup={setShowInformantSetup}
            />
          </div>
        )}
      </div>

      {/* Informant Setup Modal */}
      <AutismInformantSetupForm
        isOpen={showInformantSetup}
        onClose={() => setShowInformantSetup(false)}
      />

      {/* Manage Informants Modal */}
      {header && (
        <PatientManageInformantsForm
          isOpen={showInformantManagement}
          onClose={() => setShowInformantManagement(false)}
          patientDataModel={header}
          refreshPatientData={fetchPatientData}
        />
      )}

      {/* All Forms Completed Feedback Modal */}
      <AllFormsCompletedFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onFeedbackSubmitted={() => {
          // Refresh header data so formFeedback.submittedAt is picked up on next render
          fetchPatientData();
        }}
      />
    </div>
  );
}
